import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from '../angular-material.module';
import { LeaguePageHeroComponent } from '../shared/league-page-hero/league-page-hero.component';
import { PlayoffBracketComponent } from '../playoffs/playoff-bracket/playoff-bracket.component';
import { Tournament, TournamentBracket, TournamentGame } from './tournament.model';
import { TournamentService } from './tournament.service';
import { tournamentPlayoffGameStatus } from './tournament-game-status.util';
import { tournamentVisiblePlayoffMatchupIds } from './tournament-playoff-visibility.util';
import { TournamentGameStatsDialogComponent } from './tournament-game-stats-dialog.component';

type TournamentScheduleTimeGroup = { time: string; games: TournamentGame[] };
type TournamentSchedulePhase = { kind: 'pool' | 'playoff'; times: TournamentScheduleTimeGroup[] };

@Component({
  selector: 'app-tournament-public',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AngularMaterialModule, LeaguePageHeroComponent, PlayoffBracketComponent],
  templateUrl: './tournament-public.component.html',
  styleUrls: ['./tournament-public.component.css'],
})
export class TournamentPublicComponent implements OnInit, OnDestroy {
  private readonly divisionChipPalette = [
    { text: '#1d4ed8', background: 'rgba(59, 130, 246, .14)', border: 'rgba(59, 130, 246, .3)' },
    { text: '#047857', background: 'rgba(16, 185, 129, .14)', border: 'rgba(16, 185, 129, .28)' },
    { text: '#b45309', background: 'rgba(245, 158, 11, .16)', border: 'rgba(245, 158, 11, .3)' },
    { text: '#7c3aed', background: 'rgba(139, 92, 246, .14)', border: 'rgba(139, 92, 246, .28)' },
    { text: '#be123c', background: 'rgba(244, 63, 94, .14)', border: 'rgba(244, 63, 94, .28)' },
    { text: '#0f766e', background: 'rgba(20, 184, 166, .14)', border: 'rgba(20, 184, 166, .28)' },
    { text: '#4338ca', background: 'rgba(99, 102, 241, .14)', border: 'rgba(99, 102, 241, .28)' },
    { text: '#c2410c', background: 'rgba(249, 115, 22, .14)', border: 'rgba(249, 115, 22, .28)' },
  ];
  readonly tournamentLinks = [
    { label: 'Schedule', link: '/tournament', queryParams: { tab: 'schedule' } },
    { label: 'Bracket', link: '/tournament', queryParams: { tab: 'bracket' } },
    { label: 'Teams', link: '/tournament', queryParams: { tab: 'teams' } },
  ];
  tournaments: Tournament[] = [];
  selected: Tournament | null = null;
  isLoading = true;
  loadError = '';
  tab: 'schedule' | 'bracket' | 'teams' = 'bracket';
  bracketSection: 'schedule' | 'ranking' | 'playoff' = 'schedule';
  selectedDivisionId = '';
  selectedTeamId = '';
  teamLogoUrls: Record<string, string> = {};
  private readonly brokenLogoTeamIds = new Set<string>();
  private tournamentRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private tournamentLoadInFlight = false;
  private statsDialogRef: MatDialogRef<TournamentGameStatsDialogComponent> | null = null;

  constructor(
    private tournamentsService: TournamentService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const tab = params.get('tab');
      if (tab === 'schedule' || tab === 'bracket' || tab === 'teams') this.tab = tab;
    });
    this.loadTournaments();
    this.tournamentRefreshTimer = setInterval(() => {
      if (!document.hidden) this.loadTournaments(true);
    }, 5000);
  }

  loadTournaments(background = false): void {
    if (this.tournamentLoadInFlight) return;
    this.tournamentLoadInFlight = true;
    const selectedTournamentId = this.selected?.id || null;
    const selectedDivisionId = this.selectedDivisionId;
    const selectedTeamId = this.selectedTeamId;
    if (!background) {
      this.isLoading = true;
      this.loadError = '';
    }
    this.tournamentsService.list().subscribe({
      next: ({ tournaments }) => {
        this.tournaments = Array.isArray(tournaments) ? tournaments : [];
        this.selected = (background && selectedTournamentId
          ? this.tournaments.find((tournament) => tournament.id === selectedTournamentId)
          : null) || this.tournaments[0] || null;
        this.selectedDivisionId = background && this.selected?.divisions.some((division) => division.id === selectedDivisionId)
          ? selectedDivisionId
          : '';
        this.selectedTeamId = background && this.selected?.teams.some((team) => team.id === selectedTeamId)
          ? selectedTeamId
          : '';
        this.refreshTeamLogoUrls();
        this.isLoading = false;
        this.tournamentLoadInFlight = false;
      },
      error: () => {
        if (!background) {
          this.tournaments = [];
          this.selected = null;
          this.refreshTeamLogoUrls();
          this.loadError = 'Tournament data could not be loaded. Please try again.';
        }
        this.isLoading = false;
        this.tournamentLoadInFlight = false;
      },
    });
  }

  ngOnDestroy(): void {
    if (this.tournamentRefreshTimer !== null) clearInterval(this.tournamentRefreshTimer);
    this.statsDialogRef?.close();
  }

  games(): TournamentGame[] {
    if (!this.selected) return [];
    const regularGames = (this.selected.games || []).filter((game) =>
      (!this.selectedDivisionId || game.division_id === this.selectedDivisionId) &&
      (!this.selectedTeamId || game.home_team_id === this.selectedTeamId || game.away_team_id === this.selectedTeamId)
    );
    const playoffGames = (this.selected.brackets || [])
      .filter((bracket) => !this.selectedDivisionId || bracket.division_id === this.selectedDivisionId)
      .flatMap((bracket) => {
        const visibleMatchupIds = tournamentVisiblePlayoffMatchupIds(bracket, this.selectedTeamId);
        const division = this.selected?.divisions.find((item) => item.id === bracket.division_id);
        return bracket.matchups.filter((matchup) => visibleMatchupIds.has(matchup.id)).map((matchup): TournamentGame => {
          const gym = this.selected?.gym_locations.find((location) => location.courts?.includes(matchup.venue_court || ''));
          return {
            id: `playoff-${bracket.division_id}-${matchup.id}`,
            title: 'Playoff Game',
            round: this.playoffRoundLabel(bracket, matchup.round),
            division_id: bracket.division_id,
            division_name: division?.name || 'Tournament',
            game_date: matchup.game_date,
            game_time: matchup.game_time,
            gym_location_id: gym?.id || null,
            venue_court: matchup.venue_court || '',
            venue_school_name: matchup.venue_school_name || gym?.name || '',
            venue_address: matchup.venue_address || gym?.address || '',
            home_team_id: matchup.home_team_id,
            home_team_name: matchup.home_team_name || 'TBD',
            away_team_id: matchup.away_team_id,
            away_team_name: matchup.away_team_name || 'TBD',
            home_score: matchup.home_score,
            away_score: matchup.away_score,
            status: tournamentPlayoffGameStatus(matchup),
            is_live: matchup.is_live === true,
            period: matchup.period || null,
            game_clock: matchup.game_clock || null,
            playoffMatchupId: matchup.id,
            playoffDivisionId: bracket.division_id,
            backingScheduleId: matchup.schedule_id || null,
          };
        });
      });

    return [...regularGames, ...playoffGames];
  }

  scheduleDayGroups(): Array<{ date: string; times: Array<{ time: string; games: TournamentGame[] }> }> {
    const grouped = new Map<string, Map<string, TournamentGame[]>>();
    this.games().filter((game) => !!game.game_date).forEach((game) => {
      const date = game.game_date as string;
      const time = game.game_time || '00:00';
      const times = grouped.get(date) || new Map<string, TournamentGame[]>();
      times.set(time, [...(times.get(time) || []), game]);
      grouped.set(date, times);
    });

    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, times]) => ({
      date,
      times: [...times.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([time, games]) => ({ time, games })),
    }));
  }

  bracketScheduleDayGroups(): Array<{ date: string; times: Array<{ time: string; games: TournamentGame[] }> }> {
    const games = this.games().filter((game) => !!game.game_date);
    return this.scheduleDayGroupsForGames(games);
  }

  schedulePhases(times: TournamentScheduleTimeGroup[]): TournamentSchedulePhase[] {
    const phase = (kind: TournamentSchedulePhase['kind']): TournamentSchedulePhase => ({
      kind,
      times: times
        .map((timeGroup) => ({
          time: timeGroup.time,
          games: timeGroup.games.filter((game) => kind === 'playoff' ? !!game.playoffMatchupId : !game.playoffMatchupId),
        }))
        .filter((timeGroup) => timeGroup.games.length > 0),
    });

    return [phase('pool'), phase('playoff')].filter((item) => item.times.length > 0);
  }

  private scheduleDayGroupsForGames(games: TournamentGame[]): Array<{ date: string; times: Array<{ time: string; games: TournamentGame[] }> }> {
    const grouped = new Map<string, Map<string, TournamentGame[]>>();
    games.forEach((game) => {
      const date = game.game_date as string;
      const time = game.game_time || '00:00';
      const times = grouped.get(date) || new Map<string, TournamentGame[]>();
      times.set(time, [...(times.get(time) || []), game]);
      grouped.set(date, times);
    });
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, times]) => ({
      date,
      times: [...times.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([time, games]) => ({ time, games })),
    }));
  }

  formatDatePart(value: string, part: 'weekday' | 'date'): string {
    const date = new Date(`${value}T00:00:00`);
    return date.toLocaleDateString('en-US', part === 'weekday'
      ? { weekday: 'long' }
      : { month: 'long', day: 'numeric', year: 'numeric' });
  }

  formatGameTime(value: string): string {
    const [hours, minutes] = value.split(':').map(Number);
    const date = new Date(2000, 0, 1, hours, minutes || 0);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  formatCardDate(value: string | null): string {
    if (!value) return 'Date TBD';
    return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  teamMonogram(name: string | null | undefined): string {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return (parts[0] || '?').slice(0, 2).toUpperCase();
  }

  onTournamentChange(): void {
    this.selectedDivisionId = '';
    this.selectedTeamId = '';
    this.refreshTeamLogoUrls();
  }

  teamLogoUrl(teamId: string | null): string | null {
    if (!teamId || this.brokenLogoTeamIds.has(teamId)) return null;
    return this.teamLogoUrls[teamId] || null;
  }

  markLogoBroken(teamId: string | null): void {
    if (teamId) this.brokenLogoTeamIds.add(teamId);
  }

  private refreshTeamLogoUrls(): void {
    this.brokenLogoTeamIds.clear();
    this.teamLogoUrls = Object.fromEntries(
      (this.selected?.teams || [])
        .filter((team) => !!team.logo_url)
        .map((team) => [team.id, team.logo_url as string]),
    );
  }

  gameVenueName(game: TournamentGame): string {
    return game.venue_school_name || this.selected?.gym_locations.find((location) => location.id === game.gym_location_id)?.name || '';
  }

  gameVenueAddress(game: TournamentGame): string {
    return game.venue_address || this.selected?.gym_locations.find((location) => location.id === game.gym_location_id)?.address || '';
  }

  gamePoolName(game: TournamentGame): string {
    if (game.playoffMatchupId) return game.round || 'Playoff';
    const pool = this.selected?.pools.find((item) => item.division_id === game.division_id &&
      item.team_ids.includes(game.home_team_id || '') && item.team_ids.includes(game.away_team_id || ''));
    return pool ? this.displayPoolName(pool.pool_name, pool.division_id) : 'Pool TBD';
  }

  private playoffRoundLabel(bracket: TournamentBracket, round: number): string {
    const teamsInRound = bracket.size / Math.pow(2, round - 1);
    if (teamsInRound === 2) return 'Final';
    if (teamsInRound === 4) return 'Semifinal';
    if (teamsInRound === 8) return 'Quarterfinal';
    return `Round of ${teamsInRound}`;
  }

  gameHasFinalScore(game: TournamentGame): boolean {
    return game.status === 'completed' && game.home_score !== null && game.away_score !== null;
  }

  gameHasVisibleScore(game: TournamentGame): boolean {
    return (this.isLiveGame(game) || this.gameHasFinalScore(game)) &&
      game.home_score !== null && game.away_score !== null;
  }

  isLiveGame(game: TournamentGame): boolean {
    return game.is_live === true;
  }

  gameStatsScheduleId(game: TournamentGame): string | null {
    if (game.status === 'cancelled' || game.is_placeholder === true) return null;
    if (!game.home_team_id || !game.away_team_id || game.home_team_id === game.away_team_id) return null;
    const scheduleId = game.playoffMatchupId ? game.backingScheduleId : game.id;
    return String(scheduleId || '').trim() || null;
  }

  isGameStatsEligible(game: TournamentGame): boolean {
    return this.gameStatsScheduleId(game) !== null;
  }

  gameStatsAriaLabel(game: TournamentGame): string | null {
    if (!this.isGameStatsEligible(game)) return null;
    const time = game.game_time ? this.formatGameTime(game.game_time) : 'time TBD';
    const context = [
      game.round || this.gamePoolName(game),
      this.formatCardDate(game.game_date),
      time,
      game.venue_court || 'court TBD',
    ].filter(Boolean).join(', ');
    return `View stats for ${game.away_team_name} versus ${game.home_team_name}, ${context}`;
  }

  openGameStats(game: TournamentGame): void {
    const scheduleId = this.gameStatsScheduleId(game);
    if (!this.selected || !scheduleId || this.statsDialogRef) return;
    this.statsDialogRef = this.dialog.open(TournamentGameStatsDialogComponent, {
      data: { tournamentId: this.selected.id, scheduleId },
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      maxWidth: '96vw',
      ariaLabel: this.gameStatsAriaLabel(game) || 'Tournament game stats',
    });
    this.statsDialogRef.afterClosed().subscribe(() => { this.statsDialogRef = null; });
  }

  onGameCardKeydown(event: KeyboardEvent, game: TournamentGame): void {
    if (!this.isGameStatsEligible(game) || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    this.openGameStats(game);
  }

  livePeriodLabel(game: TournamentGame): string {
    const period = String(game.period || '').toUpperCase();
    if (period === 'H1') return '1st Half';
    if (period === 'H2') return '2nd Half';
    if (/^Q[1-4]$/.test(period)) return `Q${period.slice(1)}`;
    if (period === 'OT') return 'OT 1';
    if (/^OT\d+$/.test(period)) return `OT ${period.slice(2)}`;
    return 'In progress';
  }

  liveClock(game: TournamentGame): string {
    return /^\d{1,2}:[0-5]\d$/.test(String(game.game_clock || ''))
      ? String(game.game_clock)
      : '--:--';
  }

  divisionChipStyle(game: TournamentGame): Record<string, string> {
    const key = `${game.division_id || ''}:${game.division_name || ''}`;
    let hash = 0;
    for (let index = 0; index < key.length; index += 1) {
      hash = ((hash << 5) - hash + key.charCodeAt(index)) | 0;
    }
    const palette = this.divisionChipPalette[Math.abs(hash) % this.divisionChipPalette.length];
    return {
      '--division-chip-color': palette.text,
      '--division-chip-background': palette.background,
      '--division-chip-border': palette.border,
    };
  }

  displayPoolName(poolName: string, divisionId: string): string {
    const index = this.selected?.pools.findIndex((pool) => pool.pool_name === poolName && pool.division_id === divisionId) ?? -1;
    return index >= 0 ? `Pool ${String.fromCharCode(65 + index)}` : poolName;
  }

  private poolGames(pool: { division_id: string; pool_name: string; team_ids: string[] }): TournamentGame[] {
    return (this.selected?.games || []).filter((game) =>
      game.division_id === pool.division_id &&
      pool.team_ids.includes(game.home_team_id || '') &&
      pool.team_ids.includes(game.away_team_id || '')
    );
  }

  trackByGame(_: number, game: TournamentGame): string { return game.id; }

  trackByDivision(_: number, division: { divisionId: string }): string { return division.divisionId; }

  divisionBracket(divisionId: string): TournamentBracket | null {
    const bracket = this.selected?.brackets?.find((item) => item.division_id === divisionId);
    return bracket || null;
  }

  divisionPoolStandings(): Array<{
    name: string;
    pools: Array<{
      name: string;
      isComplete: boolean;
      rows: Array<{ rank: number; team: string; teamId: string; wins: number; losses: number; pointDiff: number }>;
    }>;
  }> {
    if (!this.selected) return [];
    const pools = (this.selected.pools || [])
      .filter((pool) => !this.selectedDivisionId || pool.division_id === this.selectedDivisionId)
      .map((pool) => {
        const poolGames = this.poolGames(pool);
        const isComplete = poolGames.length > 0 && poolGames.every((game) => this.gameHasFinalScore(game));
        const rows = pool.team_ids
          .map((teamId) => {
            const team = this.selected?.teams.find((item) => item.id === teamId);
            const stats = { teamId, team: team?.name || pool.team_names?.[pool.team_ids.indexOf(teamId)] || 'Team TBD', wins: 0, losses: 0, pointDiff: 0 };
            poolGames
              .filter((game) => game.status === 'completed' && game.home_score !== null && game.away_score !== null &&
                (game.home_team_id === teamId || game.away_team_id === teamId))
              .forEach((game) => {
                const isHome = game.home_team_id === teamId;
                const teamScore = Number(isHome ? game.home_score : game.away_score);
                const opponentScore = Number(isHome ? game.away_score : game.home_score);
                stats.pointDiff += teamScore - opponentScore;
                if (teamScore > opponentScore) stats.wins += 1;
                if (teamScore < opponentScore) stats.losses += 1;
              });
            return stats;
          })
          .sort((a, b) => b.wins - a.wins || b.pointDiff - a.pointDiff || a.team.localeCompare(b.team))
          .map((row, index) => ({ ...row, rank: index + 1 }));
        return { division_id: pool.division_id, name: this.displayPoolName(pool.pool_name, pool.division_id), isComplete, rows };
      })
      .filter((pool) => pool.rows.length > 0);

    return this.selected.divisions
      .filter((division) => pools.some((pool) => pool.division_id === division.id))
      .map((division) => ({
        divisionId: division.id,
        name: division.name,
        pools: pools.filter((pool) => pool.division_id === division.id),
      }));
  }

  teams() {
    return (this.selected?.teams || []).filter((team) => !this.selectedDivisionId || team.division_id === this.selectedDivisionId);
  }
}
